import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FiDownload, FiSearch, FiBookOpen, FiPackage } from 'react-icons/fi';

const Releases = () => {
  const [manifest, setManifest] = useState(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${process.env.PUBLIC_URL || ''}/release-manifest.json`)
      .then((response) => response.json())
      .then((data) => setManifest(data))
      .catch(() => setManifest(null));
  }, []);

  const filteredDocs = useMemo(() => {
    const docs = manifest?.docs || [];
    if (!query.trim()) return docs;
    const normalized = query.trim().toLowerCase();
    return docs.filter((doc) =>
      `${doc.title} ${doc.summary} ${doc.kind}`.toLowerCase().includes(normalized),
    );
  }, [manifest, query]);

  const downloads = manifest?.downloads || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-900 via-dark-800 to-secondary-900 text-white">
      <Header />
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-cyan-200">
              <FiPackage />
              Release Center
            </div>
            <h1 className="mt-6 text-5xl font-display font-bold gradient-text">Downloads, Releases, and Docs</h1>
            <p className="mt-4 max-w-3xl text-lg text-slate-300">
              One place to see the current Bervin release, supported downloads, and the fastest guides for getting your team productive.
            </p>
          </motion.div>

          <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur-xl">
              <div className="flex items-center gap-3">
                <FiDownload className="text-cyan-300" />
                <h2 className="text-2xl font-bold">Release Matrix</h2>
              </div>
              <p className="mt-3 text-slate-300">
                Current coordinated release version: <strong>{manifest?.version || 'Loading...'}</strong>
              </p>
              <div className="mt-6 space-y-4">
                {Object.entries(downloads).map(([key, item]) => (
                  <a
                    key={key}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block rounded-2xl border border-white/10 bg-slate-950/40 p-5 transition hover:border-cyan-400/40 hover:bg-slate-950/60"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-semibold text-white">{item.label}</h3>
                        <p className="mt-1 text-sm text-slate-300">Version {item.version}</p>
                        <p className="mt-1 text-sm text-slate-400">{item.requirements}</p>
                      </div>
                      <span className="rounded-full border border-white/10 px-3 py-1 text-xs text-slate-300">{item.size}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur-xl">
              <div className="flex items-center gap-3">
                <FiBookOpen className="text-cyan-300" />
                <h2 className="text-2xl font-bold">Search Docs</h2>
              </div>
              <div className="relative mt-6">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search guides and setup docs"
                  className="w-full rounded-2xl border border-white/10 bg-slate-950/40 py-3 pl-12 pr-4 text-white outline-none transition focus:border-cyan-400/50"
                />
              </div>
              <div className="mt-6 space-y-4">
                {filteredDocs.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-white/10 p-6 text-center text-slate-400">
                    No docs matched that search yet.
                  </div>
                ) : (
                  filteredDocs.map((doc) => (
                    <a
                      key={doc.path}
                      href={doc.path}
                      className="block rounded-2xl border border-white/10 bg-slate-950/40 p-5 transition hover:border-cyan-400/40 hover:bg-slate-950/60"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <p className="text-xs uppercase tracking-[0.2em] text-cyan-300">{doc.kind}</p>
                          <h3 className="mt-2 text-lg font-semibold text-white">{doc.title}</h3>
                          <p className="mt-2 text-sm text-slate-300">{doc.summary}</p>
                        </div>
                      </div>
                    </a>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Releases;
